import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createSuperOverPromotion,
  deleteSuperOverPromotion,
  getSuperOverPromotion,
  getSuperOverPromotionsPayload,
  isSuperOverPromotionsApiSuccess,
  listSuperOverPromotions,
  patchSuperOverPromotion,
  previewSuperOverPromotions,
} from '../../services/superOverPromotionsApi';
import {
  buildPromoPayload,
  computePromoStatus,
  emptyPromoForm,
  formatEntryFee,
  formatFe,
  formatIstDateTime,
  istDatetimeLocalToIso,
  MODE_LABELS,
  normalizeDefaults,
  normalizePreviewModes,
  previewModeSummary,
  PROMO_MODES,
  promoToForm,
  STATUS_FILTER_OPTIONS,
  statusChipColor,
  statusChipLabel,
} from './superOverPromotionsUtils';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const PAGE_SIZE = 50;

function PreviewPanel({ title, atIso, loading, error, modes, defaults }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {atIso && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          at {formatIstDateTime(atIso)}
        </Typography>
      )}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading preview…
          </Typography>
        </Box>
      )}
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {PROMO_MODES.map((mode) => {
            const summary = previewModeSummary(modes[mode]);
            const baseline = defaults?.[mode];
            return (
              <Box key={mode} sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {MODE_LABELS[mode]}
                </Typography>
                {summary ? (
                  <>
                    <Typography variant="body2">
                      Entry: {formatEntryFee(summary.fee)}
                      {' · '}Win: {formatFe(summary.reward)}
                      {mode !== 'daily' ? ` · Tie: ${formatFe(summary.tieReward)}` : ''}
                      {' · '}Lose: {formatFe(summary.loseFe ?? baseline?.lose_fe ?? 0)}
                      {' · '}Forfeit: {formatFe(summary.forfeitFe ?? baseline?.forfeit_fe ?? 0)}
                    </Typography>
                    {summary.promoName ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Promo: {summary.promoName}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Baseline (no active promo)
                        {baseline
                          ? ` — entry ${formatFe(baseline.entry_fee_fe, { freeLabel: true })}, win ${formatFe(baseline.reward_fe)}`
                          : ''}
                      </Typography>
                    )}
                    {(summary.title || summary.subtitle || summary.cta) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[summary.title, summary.subtitle, summary.cta].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No promo override
                    {baseline
                      ? ` — baseline entry ${formatFe(baseline.entry_fee_fe, { freeLabel: true })}, win ${formatFe(baseline.reward_fe)}`
                      : ' (baseline pricing)'}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

function PromoFormDrawer({
  open,
  mode,
  form,
  setForm,
  saving,
  formError,
  onClose,
  onSave,
  defaults,
}) {
  const [previewNow, setPreviewNow] = useState({ loading: false, error: '', modes: {} });
  const [previewStart, setPreviewStart] = useState({ loading: false, error: '', modes: {} });

  const startsAtIso = useMemo(() => istDatetimeLocalToIso(form.starts_at), [form.starts_at]);

  const fetchPreview = useCallback(async (atIso, setter) => {
    if (!atIso) {
      setter({ loading: false, error: 'Set a valid start time for window preview.', modes: {} });
      return;
    }
    setter((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await previewSuperOverPromotions(atIso);
      if (!isSuperOverPromotionsApiSuccess(res)) {
        setter({ loading: false, error: res?.message || 'Preview failed.', modes: {} });
        return;
      }
      const data = getSuperOverPromotionsPayload(res);
      setter({ loading: false, error: '', modes: normalizePreviewModes(data) });
    } catch {
      setter({ loading: false, error: 'Preview failed.', modes: {} });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchPreview(new Date().toISOString(), setPreviewNow);
  }, [open, fetchPreview]);

  useEffect(() => {
    if (!open || !startsAtIso) {
      setPreviewStart({ loading: false, error: '', modes: {} });
      return;
    }
    const timer = setTimeout(() => {
      fetchPreview(startsAtIso, setPreviewStart);
    }, 400);
    return () => clearTimeout(timer);
  }, [open, startsAtIso, fetchPreview]);

  const toggleMode = (modeKey) => {
    setForm((prev) => {
      const has = prev.modes.includes(modeKey);
      const nextModes = has ? prev.modes.filter((m) => m !== modeKey) : [...prev.modes, modeKey];
      return { ...prev, modes: nextModes };
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 520 }, p: 0 } }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto' }}>
        <Typography variant="h6">{mode === 'create' ? 'Create promotion' : 'Edit promotion'}</Typography>
        <Typography variant="body2" color="text.secondary">
          Schedule entry / win / tie / lose / forfeit FE overrides for Super Over modes. Times are IST; economy is
          stamped onto each match at start (~5–30s to apply). Card image and badge stay on the base card.
        </Typography>

        {formError && <Alert severity="error">{formError}</Alert>}

        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          fullWidth
          disabled={saving}
          helperText="Admin label (not shown in app)"
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Modes
          </Typography>
          <FormGroup row>
            {PROMO_MODES.map((modeKey) => (
              <FormControlLabel
                key={modeKey}
                control={
                  <Checkbox
                    checked={form.modes.includes(modeKey)}
                    onChange={() => toggleMode(modeKey)}
                    disabled={saving}
                  />
                }
                label={MODE_LABELS[modeKey]}
              />
            ))}
          </FormGroup>
        </Box>

        <TextField
          label="Entry fee (FE)"
          type="number"
          value={form.entry_fee_fe}
          onChange={(e) => setForm((prev) => ({ ...prev, entry_fee_fe: e.target.value }))}
          required
          fullWidth
          disabled={saving}
          inputProps={{ min: 0, step: 1 }}
          helperText="0 = free while promo is active"
        />
        <TextField
          label="Win reward (FE)"
          type="number"
          value={form.reward_fe}
          onChange={(e) => setForm((prev) => ({ ...prev, reward_fe: e.target.value }))}
          fullWidth
          disabled={saving}
          inputProps={{ min: 0, step: 1 }}
          helperText="Default 50 if omitted on create. While active, win FE comes from this promo (not a mix with baseline)."
        />
        <TextField
          label="Tie reward (FE)"
          type="number"
          value={form.tie_reward_fe}
          onChange={(e) => setForm((prev) => ({ ...prev, tie_reward_fe: e.target.value }))}
          fullWidth
          disabled={saving}
          inputProps={{ min: 0, step: 1 }}
          helperText="Default 25 if omitted on create. Daily mode typically has no tie (0)."
        />
        <TextField
          label="Lose fee (FE)"
          type="number"
          value={form.lose_fe}
          onChange={(e) => setForm((prev) => ({ ...prev, lose_fe: e.target.value }))}
          fullWidth
          disabled={saving}
          inputProps={{ min: 0, step: 1 }}
          helperText="FE deducted on a completed fair loss. Default 0."
        />
        <TextField
          label="Forfeit fee (FE)"
          type="number"
          value={form.forfeit_fe}
          onChange={(e) => setForm((prev) => ({ ...prev, forfeit_fe: e.target.value }))}
          fullWidth
          disabled={saving}
          inputProps={{ min: 0, step: 1 }}
          helperText={
            Number(form.entry_fee_fe) === 0
              ? 'Recommended 25 for free-entry promos so quitting mid-match still costs FE.'
              : 'FE deducted on forfeit (quit / disconnect / no-response). Default 0.'
          }
        />

        <TextField
          label="Starts at (IST)"
          type="datetime-local"
          value={form.starts_at}
          onChange={(e) => setForm((prev) => ({ ...prev, starts_at: e.target.value }))}
          required
          fullWidth
          disabled={saving}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Ends at (IST)"
          type="datetime-local"
          value={form.ends_at}
          onChange={(e) => setForm((prev) => ({ ...prev, ends_at: e.target.value }))}
          required
          fullWidth
          disabled={saving}
          InputLabelProps={{ shrink: true }}
          helperText="Active window: starts_at ≤ now &lt; ends_at"
        />

        <FormControlLabel
          control={
            <Switch
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              disabled={saving}
            />
          }
          label="Enabled"
        />

        <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Browse card copy (optional)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Title, subtitle, and CTA only — card image and badge are not promo-overridable.
            </Typography>
            <TextField
              label="Card title"
              value={form.card_title}
              onChange={(e) => setForm((prev) => ({ ...prev, card_title: e.target.value }))}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Card subtitle"
              value={form.card_subtitle}
              onChange={(e) => setForm((prev) => ({ ...prev, card_subtitle: e.target.value }))}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="CTA label"
              value={form.cta_label}
              onChange={(e) => setForm((prev) => ({ ...prev, cta_label: e.target.value }))}
              fullWidth
              disabled={saving}
              placeholder="Play free"
            />
          </AccordionDetails>
        </Accordion>

        <PreviewPanel
          title="Preview — now"
          atIso={new Date().toISOString()}
          loading={previewNow.loading}
          error={previewNow.error}
          modes={previewNow.modes}
          defaults={defaults}
        />
        <PreviewPanel
          title="Preview — at window start"
          atIso={startsAtIso}
          loading={previewStart.loading}
          error={previewStart.error}
          modes={previewStart.modes}
          defaults={defaults}
        />

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving} fullWidth>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={saving} fullWidth>
            {saving ? <CircularProgress size={22} /> : mode === 'create' ? 'Create' : 'Save changes'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function SuperOverPromotions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [defaults, setDefaults] = useState(() => normalizeDefaults(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [offset, setOffset] = useState(0);

  const [listPreview, setListPreview] = useState({ loading: false, error: '', modes: {} });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPromoForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      const code = String(res?.statusCode || res?.status_code || '');
      if (code === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const handleApiError = useCallback(
    (res, fallback) => {
      if (handleSessionExpired(res)) return true;
      if (!isSuperOverPromotionsApiSuccess(res)) {
        setError(res?.message || fallback);
        return true;
      }
      return false;
    },
    [handleSessionExpired]
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listSuperOverPromotions({
        status: statusFilter,
        limit: PAGE_SIZE,
        offset,
      });
      if (handleApiError(res, 'Failed to load promotions.')) {
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getSuperOverPromotionsPayload(res);
      setRows(data?.rows || data?.items || (Array.isArray(data) ? data : []));
      setTotal(data?.total ?? (Array.isArray(data) ? data.length : 0));
      setDefaults(normalizeDefaults(data));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load promotions.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [handleApiError, offset, statusFilter]);

  const fetchListPreview = useCallback(async () => {
    setListPreview((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await previewSuperOverPromotions(new Date().toISOString());
      if (handleSessionExpired(res)) return;
      if (!isSuperOverPromotionsApiSuccess(res)) {
        setListPreview({ loading: false, error: res?.message || 'Preview unavailable.', modes: {} });
        return;
      }
      const data = getSuperOverPromotionsPayload(res);
      setListPreview({ loading: false, error: '', modes: normalizePreviewModes(data) });
    } catch {
      setListPreview({ loading: false, error: 'Preview unavailable.', modes: {} });
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchListPreview();
  }, [fetchListPreview]);

  const hasMore = offset + rows.length < total;
  const hasPrev = offset > 0;

  const openCreate = () => {
    setDrawerMode('create');
    setEditingId(null);
    setForm(emptyPromoForm());
    setFormError('');
    setDrawerOpen(true);
  };

  const openEdit = async (row) => {
    setDrawerMode('edit');
    setEditingId(row.id);
    setFormError('');
    setDrawerOpen(true);
    setLoadingEdit(true);
    try {
      const res = await getSuperOverPromotion(row.id);
      if (handleSessionExpired(res)) return;
      if (!isSuperOverPromotionsApiSuccess(res)) {
        setFormError(res?.message || 'Failed to load promotion.');
        setForm(promoToForm(row));
        return;
      }
      const data = getSuperOverPromotionsPayload(res);
      setForm(promoToForm(data || row));
    } catch {
      setFormError('Failed to load promotion.');
      setForm(promoToForm(row));
    } finally {
      setLoadingEdit(false);
    }
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setFormError('');
  };

  const handleSave = async () => {
    setFormError('');
    const built = buildPromoPayload(form);
    if (built.error) {
      setFormError(built.error);
      return;
    }

    setSaving(true);
    try {
      const res =
        drawerMode === 'create'
          ? await createSuperOverPromotion(built.payload)
          : await patchSuperOverPromotion(editingId, built.payload);

      if (handleSessionExpired(res)) return;
      if (!isSuperOverPromotionsApiSuccess(res)) {
        setFormError(res?.message || 'Save failed.');
        return;
      }

      showSnackbar(drawerMode === 'create' ? 'Promotion created.' : 'Promotion updated.');
      setDrawerOpen(false);
      fetchList();
      fetchListPreview();
    } catch {
      setFormError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (row) => {
    const nextEnabled = !row.enabled;

    try {
      const res = await patchSuperOverPromotion(row.id, { enabled: nextEnabled });
      if (handleSessionExpired(res)) return;
      if (!isSuperOverPromotionsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update.', 'error');
        return;
      }
      const updated = getSuperOverPromotionsPayload(res);
      setRows((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, ...(updated || {}), enabled: nextEnabled } : item))
      );
      showSnackbar(nextEnabled ? 'Promotion enabled.' : 'Promotion disabled.');
      fetchListPreview();
    } catch {
      showSnackbar('Failed to update.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteSuperOverPromotion(deleteTarget.id);
      if (handleSessionExpired(res)) return;
      if (!isSuperOverPromotionsApiSuccess(res)) {
        showSnackbar(res?.message || 'Delete failed.', 'error');
        return;
      }
      showSnackbar('Promotion deleted.');
      setDeleteTarget(null);
      fetchList();
      fetchListPreview();
    } catch {
      showSnackbar('Delete failed.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
            <Button component={Link} to="/admin/arcade" startIcon={<ArrowBackIcon />} size="small">
              Arcade
            </Button>
            <Typography variant="h5" sx={{ flex: 1 }}>
              Super Over Promotions
            </Typography>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => {
                  fetchList();
                  fetchListPreview();
                }}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create promotion
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Schedule <strong>entry / win / tie / lose / forfeit</strong> FE overrides and optional browse-card copy
            (title, subtitle, CTA) for Super Over match, league, and daily. Card image and badge stay on the base card.
            While a promo is active, full economy comes from the promo row. Mid-match edits do not change that
            match&apos;s settlement. Overlapping promos: most recently updated wins. Needs migrations 209 / 210 / 212 /
            218.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                select
                label="Status"
                size="small"
                value={statusFilter}
                onChange={(e) => {
                  setOffset(0);
                  setStatusFilter(e.target.value);
                }}
                sx={{ minWidth: 160 }}
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Chip size="small" label={`${total} total`} variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                Baseline match: entry {formatFe(defaults.match?.entry_fee_fe, { freeLabel: true })} · win{' '}
                {formatFe(defaults.match?.reward_fe)} · tie {formatFe(defaults.match?.tie_reward_fe)} · lose{' '}
                {formatFe(defaults.match?.lose_fe ?? 0)} · forfeit {formatFe(defaults.match?.forfeit_fe ?? 0)}
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' }, gap: 2, mb: 2 }}>
            <Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Modes</TableCell>
                          <TableCell align="right">Entry</TableCell>
                          <TableCell align="right">Win / Tie</TableCell>
                          <TableCell align="right">Lose / Forfeit</TableCell>
                          <TableCell>Window (IST)</TableCell>
                          <TableCell>Enabled</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">No promotions in this filter.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          rows.map((row) => {
                            const status = computePromoStatus(row);
                            return (
                              <TableRow key={row.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {row.name}
                                  </Typography>
                                  {row.card_subtitle && (
                                    <Typography variant="caption" color="text.secondary" noWrap title={row.card_subtitle}>
                                      {row.card_subtitle}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={statusChipLabel(status)}
                                    color={statusChipColor(status)}
                                    variant={status === 'past' ? 'outlined' : 'filled'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(row.modes || []).map((m) => (
                                      <Chip key={m} size="small" label={MODE_LABELS[m] || m} variant="outlined" />
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">{formatEntryFee(row.entry_fee_fe)}</TableCell>
                                <TableCell align="right">
                                  {formatFe(row.reward_fe ?? 50)} / {formatFe(row.tie_reward_fe ?? 25)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatFe(row.lose_fe ?? 0)} / {formatFe(row.forfeit_fe ?? 0)}
                                </TableCell>
                                <TableCell sx={{ minWidth: 200 }}>
                                  <Typography variant="body2">{formatIstDateTime(row.starts_at)}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    → {formatIstDateTime(row.ends_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Switch
                                    size="small"
                                    checked={Boolean(row.enabled)}
                                    onChange={() => handleQuickToggle(row)}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteTarget(row)}
                                    aria-label="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button disabled={!hasPrev} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {total > 0 ? `${offset + 1}–${offset + rows.length} of ${total}` : '0 results'}
                    </Typography>
                    <Button disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                      Next
                    </Button>
                  </Box>
                </>
              )}
            </Box>

            <PreviewPanel
              title="Effective pricing — now"
              atIso={new Date().toISOString()}
              loading={listPreview.loading}
              error={listPreview.error}
              modes={listPreview.modes}
              defaults={defaults}
            />
          </Box>
        </div>
      </div>

      <PromoFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        form={form}
        setForm={setForm}
        saving={saving || loadingEdit}
        formError={formError}
        onClose={closeDrawer}
        onSave={handleSave}
        defaults={defaults}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete promotion?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete &ldquo;{deleteTarget?.name}&rdquo;? Prefer disabling if you want to keep history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={22} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default SuperOverPromotions;
